import type { CostActual } from '~/features/cost-estimate/types/cost-estimate.types';

/** A batch's recorded spend, read per run: the sample-enrichment run and the full (pipeline) run. */
export interface BatchActuals {
  sample: CostActual | null;
  fullRun: CostActual | null;
}

/** Content's two halves, for the expanded row's "Sample $X · Full run $Y". */
export interface ContentSplit {
  sampleUsd: number | null;
  fullRunUsd: number | null;
  /** The full run enriched products but recorded no cost: it reused earlier results ($0). */
  fullRunReused: boolean;
}

/** One row of the breakdown: a part of the run and what it has cost so far (null = no data). */
export interface CostPart {
  key: string;
  /** `run-wizard` key under `review.cost.lines`, or null for the remainder row. */
  lineKey: string | null;
  totalUsd: number | null;
  perProductUsd: number | null;
  split?: ContentSplit;
}

const LINE_KEYS = [
  'content',
  'searchDescriptors',
  'phraseDescriptions',
  'scoringBefore',
  'scoringAfter',
  'translation',
] as const;

/** Spend and products of a source's part; null when that source recorded nothing for it. */
type Spend = { costUsd: number; products: number } | null;

const sumOf = (values: (number | null)[]) => {
  const known = values.filter((v): v is number => v !== null);
  return known.length > 0 ? known.reduce((n, v) => n + v, 0) : null;
};

/** The ledger step kinds that pass `match`, summed; null when none recorded. */
function stepSpend(actual: CostActual, match: (kind: string) => boolean): Spend {
  const steps = actual.steps.filter((s) => match(s.kind));
  if (steps.length === 0) return null;
  return {
    costUsd: steps.reduce((n, s) => n + s.costUsd, 0),
    products: Math.max(...steps.map((s) => s.products)),
  };
}

/**
 * One run's spend per non-content part, from the source workflow-service attributes it to
 * (`CostActualDto`): search descriptors = the phrase-run table, phrase descriptions / scoring =
 * their `STEP_*` ledger kinds, translation = `STEP_TRANSLATION_*` or the localized-rows fallback.
 */
function partSpend(actual: CostActual, key: (typeof LINE_KEYS)[number]): Spend {
  const enriched = actual.enrichedProducts;
  switch (key) {
    case 'searchDescriptors':
      return actual.searchDescriptorsUsd === null
        ? null
        : { costUsd: actual.searchDescriptorsUsd, products: enriched };
    case 'phraseDescriptions':
      return stepSpend(actual, (k) => k === 'STEP_PHRASE_DESCRIPTIONS_POST');
    case 'scoringBefore':
      return stepSpend(actual, (k) => k === 'STEP_SCORING_PRE');
    case 'scoringAfter':
      return stepSpend(actual, (k) => k === 'STEP_SCORING_POST');
    case 'translation':
      return (
        stepSpend(actual, (k) => k.startsWith('STEP_TRANSLATION_')) ??
        (actual.translationUsd === null
          ? null
          : { costUsd: actual.translationUsd, products: enriched })
      );
    default:
      return null;
  }
}

/** A batch-level actual's runs of one kind (`runs[]`, workflow #1054); empty when not sent. */
function runsOf(actual: CostActual | null, kind: string) {
  return (actual?.runs ?? []).filter((r) => r.kind === kind);
}

/** Products the run covers: the full run's, else the sample's. Per-product content divides by it. */
export function runProducts({ sample, fullRun }: BatchActuals): number {
  const fullRuns = runsOf(fullRun, 'FULL_RUN');
  if (!sample && fullRuns.length > 0) {
    const full = fullRuns.reduce((n, r) => n + r.enrichedProducts, 0);
    if (full > 0) return full;
  }
  const full = fullRun?.enrichedProducts ?? 0;
  return full > 0 ? full : (sample?.enrichedProducts ?? 0);
}

/** Recorded spend across both runs; null when neither run has an actual. */
export function spentUsd({ sample, fullRun }: BatchActuals): number | null {
  return sumOf([sample?.totalUsd ?? null, fullRun?.totalUsd ?? null]);
}

/**
 * The batch's recorded spend by part, in the Review breakdown's order. Content = the sample's
 * enrichment plus the full run's, spread over the full run's products (as the Review's per-product
 * rate is). A full run that enriched products with no recorded cost reused earlier results: $0.
 * Whatever the totals hold beyond these parts (data scoring, gap analysis, re-enrichment) is
 * "Other". A part with no data is null ("—").
 */
export function costParts(actuals: BatchActuals): CostPart[] {
  const { sample, fullRun } = actuals;
  const products = runProducts(actuals);
  // Read by batch id, one actual covers every run and names them in `runs[]`: the split comes
  // from there. Read by run ids, the sample and the full run are separate actuals.
  const sampleRuns = sample ? [] : runsOf(fullRun, 'SAMPLE');
  const fullRuns = sample ? [] : runsOf(fullRun, 'FULL_RUN');
  const byRuns = sampleRuns.length > 0 || fullRuns.length > 0;
  let sampleUsd: number | null;
  let fullRunUsd: number | null;
  let fullRunReused: boolean;
  let contentUsd: number | null;
  if (byRuns) {
    sampleUsd = sampleRuns.length > 0 ? (sumOf(sampleRuns.map((r) => r.enrichmentUsd)) ?? 0) : null;
    const fullEnriched = fullRuns.reduce((n, r) => n + r.enrichedProducts, 0);
    fullRunReused = fullEnriched > 0 && fullRuns.every((r) => r.productsWithCost === 0);
    fullRunUsd = fullRuns.length > 0 ? (sumOf(fullRuns.map((r) => r.enrichmentUsd)) ?? 0) : null;
    contentUsd = fullRun && fullRun.enrichedProducts > 0 ? fullRun.enrichmentUsd : null;
  } else {
    sampleUsd = sample && sample.enrichedProducts > 0 ? sample.enrichmentUsd : null;
    fullRunUsd = fullRun && fullRun.enrichedProducts > 0 ? fullRun.enrichmentUsd : null;
    fullRunReused = !!fullRun && fullRun.enrichedProducts > 0 && fullRun.productsWithCost === 0;
    contentUsd = sumOf([sampleUsd, fullRunUsd]);
  }
  const parts: CostPart[] = [
    {
      key: 'content',
      lineKey: 'content',
      totalUsd: contentUsd,
      perProductUsd: contentUsd !== null && products > 0 ? contentUsd / products : null,
      ...(sample || byRuns || fullRunReused
        ? { split: { sampleUsd, fullRunUsd, fullRunReused } }
        : {}),
    },
  ];
  for (const key of LINE_KEYS.slice(1)) {
    const spends = [sample, fullRun]
      .filter((a): a is CostActual => a !== null)
      .map((a) => partSpend(a, key))
      .filter((s): s is NonNullable<Spend> => s !== null);
    const totalUsd = sumOf(spends.map((s) => s.costUsd));
    const covered = spends.reduce((n, s) => n + s.products, 0);
    parts.push({
      key,
      lineKey: key,
      totalUsd,
      perProductUsd: totalUsd !== null && covered > 0 ? totalUsd / covered : null,
    });
  }
  const rest = (spentUsd(actuals) ?? 0) - parts.reduce((n, p) => n + (p.totalUsd ?? 0), 0);
  // Cents of float drift are not a part.
  if (rest > 0.005)
    parts.push({ key: 'other', lineKey: null, totalUsd: rest, perProductUsd: null });
  return parts;
}

/** The breakdown's part key for each planned / rate kind. */
const PART_OF_KIND: Record<string, string> = {
  CONTENT: 'content',
  SEARCH_DESCRIPTORS: 'searchDescriptors',
  PHRASE_DESCRIPTIONS: 'phraseDescriptions',
  SCORING_PRE: 'scoringBefore',
  SCORING_POST: 'scoringAfter',
  TRANSLATION: 'translation',
};

/** Planned (or recomputed) line totals by breakdown part; a line with no amount is left out. */
export function plannedByPart(
  lines: readonly { kind: string; totalUsd: number | null }[] | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const l of lines ?? []) {
    const key = PART_OF_KIND[l.kind];
    if (key && l.totalUsd !== null) out[key] = (out[key] ?? 0) + l.totalUsd;
  }
  return out;
}
